// frontend/src/pages/admin/AdminInventoryReports.js
import React, { useState, useEffect } from 'react';
import { inventoryApi } from '../../api/inventory';
import helpers from '../../utils/helpers';
import { FaChartBar, FaExclamationTriangle, FaTimesCircle, FaBoxOpen, FaSpinner } from 'react-icons/fa';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const COLORS = ['#EF4444', '#F59E0B', '#10B981']; // Red, Yellow, Green

function AdminInventoryReports() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await inventoryApi.getInventoryStats();
        setStats(data);
      } catch (err) {
        console.error("Failed to load stats", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <div className="p-8 text-center"><FaSpinner className="animate-spin text-2xl text-primary" /> Loading Reports...</div>;
  if (!stats) return <div className="p-8 text-center">No data available.</div>;

  // Prepare Chart Data
  const chartData = [
    { name: 'Out of Stock', value: stats.out_of_stock_count },
    { name: 'Low Stock', value: stats.low_stock_count },
    { name: 'Healthy Stock', value: stats.total_items - (stats.out_of_stock_count + stats.low_stock_count) }
  ];

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
        <FaChartBar className="text-primary" /> Inventory Optimization
      </h1>

      {/* KPI CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-blue-500">
          <p className="text-xs font-bold text-gray-500 uppercase mb-1">Total Inventory Value</p>
          {/* UPDATED: Responsive font size and break-words */}
          <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 break-words">
            {helpers.formatCurrency(stats.total_value, 'NGN')}
          </h3>
          <p className="text-xs text-gray-400 mt-1">Based on current retail price</p>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-red-500">
          <p className="text-xs font-bold text-gray-500 uppercase mb-1">Out of Stock Items</p>
          <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-red-600">
            {stats.out_of_stock_count}
          </h3>
          <p className="text-xs text-gray-400 mt-1">Items with 0 quantity</p>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-yellow-500">
          <p className="text-xs font-bold text-gray-500 uppercase mb-1">Low Stock Alerts</p>
          <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-yellow-600">
            {stats.low_stock_count}
          </h3>
          <p className="text-xs text-gray-400 mt-1">Items below 5 units</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* CHART */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="font-bold text-gray-700 mb-4">Stock Health Distribution</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ACTIONABLE LIST */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 overflow-y-auto max-h-[350px]">
          <h3 className="font-bold text-gray-700 mb-4">Restock Recommendations</h3>
          
          {stats.out_of_stock_list.length === 0 && stats.low_stock_list.length === 0 ? (
              <p className="text-green-600 text-sm">Inventory is healthy!</p>
          ) : (
              <div className="space-y-3">
                  {stats.out_of_stock_list.map(item => (
                      <div key={item.id} className="flex justify-between items-center p-3 bg-red-50 rounded border border-red-100">
                          <div className="flex items-center gap-2">
                              <FaTimesCircle className="text-red-500" />
                              <div>
                                  <p className="text-sm font-bold text-gray-800">{item.name}</p>
                                  <p className="text-xs text-red-600">Out of Stock</p>
                              </div>
                          </div>
                          <span className="text-xs font-mono font-bold">{helpers.formatCurrency(item.price, 'NGN')}</span>
                      </div>
                  ))}
                  {stats.low_stock_list.map(item => (
                      <div key={item.id} className="flex justify-between items-center p-3 bg-yellow-50 rounded border border-yellow-100">
                          <div className="flex items-center gap-2">
                              <FaExclamationTriangle className="text-yellow-500" />
                              <div>
                                  <p className="text-sm font-bold text-gray-800">{item.name}</p>
                                  <p className="text-xs text-yellow-700">Low Stock: {item.stock}</p>
                              </div>
                          </div>
                          <span className="text-xs font-mono font-bold">{helpers.formatCurrency(item.price, 'NGN')}</span>
                      </div>
                  ))}
              </div>
          )}
        </div>

      </div>
    </div>
  );
}

export default AdminInventoryReports;